import { EntityScope } from '../../../../shared/utils/enums';
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthFetchFromRestAPI } from '../../util/api';
import { EnduranceTrainingTypeDto } from '../../../../shared/dtos/enduranceTrainingType.dto';
import { UnitDto } from '../../../../shared/dtos/unit.dto';
import { Tab, Tabs } from 'react-bootstrap';
import PrimaryButton from '../PrimaryButton';
import i18next from 'i18next';
import EnduranceTrainingTypeModal from './EnduranceTrainingTypeModal';
import EnduranceTrainingTypeTable from './EnduranceTrainingTypeTable';
import { ManagementMode, ScopeKey } from '../../util/types';

type EnduranceTrainingTypeManagementProps = {
  mode: ManagementMode;
}

function isScopeTabKey(value: string | null): value is ScopeKey {
  return value === 'tenant' || value === 'core';
}

function getTypesEndPoint(mode: ManagementMode): string {
  if (mode === 'superAdmin') {
    return '/api/v1/endurance-training-type/core';
  }
  
  return '/api/v1/endurance-training-type';
}

function getModalEntityScope(mode: ManagementMode): EntityScope {
  if (mode === 'superAdmin') {
    return EntityScope.CORE;
  }
  
  return EntityScope.TENANT;
}

const EnduranceTrainingTypeManagement: FC<EnduranceTrainingTypeManagementProps> = ({ mode }: EnduranceTrainingTypeManagementProps): ReactElement => {
  const { authFetch } = useAuthFetchFromRestAPI();
  
  const [types, setTypes] = useState<EnduranceTrainingTypeDto[]>([]);
  const [units, setUnits] = useState<UnitDto[]>([]);
  const [activeKey, setActiveKey] = useState<ScopeKey>('tenant');
  
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  
  const [showModal, setShowModal] = useState(false);
  const [editType, setEditType] = useState<EnduranceTrainingTypeDto | null>(null);
  
  const isLoading: boolean = isLoadingTypes || isLoadingUnits;
  const typesEndpoint: string = getTypesEndPoint(mode);
  const modalEntityScope: EntityScope = getModalEntityScope(mode);
  const isAdminMode: boolean = mode === 'admin';
  
  const { tenantTypes, coreTypes } = useMemo(() => {
    const tenant: EnduranceTrainingTypeDto[] = types.filter((type: EnduranceTrainingTypeDto) => type.scope === EntityScope.TENANT);
    const core: EnduranceTrainingTypeDto[] = types.filter((type: EnduranceTrainingTypeDto) => type.scope === EntityScope.CORE);
    
    return { tenantTypes: tenant, coreTypes: core };
  }, [types]);
  
  const fetchUnits: () => Promise<void> = useCallback(async () => {
    setIsLoadingUnits(true);
    
    try {
      const result: UnitDto[] = await authFetch('api/v1/unit', { method: 'GET' });
      setUnits(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingUnits(false);
    }
  }, [authFetch])
  
  const fetchTypes: () => Promise<void> = useCallback(async () => {
    setIsLoadingTypes(true);
    
    try {
      const result: EnduranceTrainingTypeDto[] = await authFetch(typesEndpoint, { method: 'GET' });
      setTypes(result);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingTypes(false);
    }
  }, [authFetch, typesEndpoint]);

  useEffect(() => {
    void fetchTypes();
  }, [fetchTypes]);
  
  useEffect(() => {
    void fetchUnits();
  }, [fetchUnits]);
  
  const closeModal = (): void => {
    setShowModal(false);
    setEditType(null);
  }
  
  const openCreateModal = (): void => {
    setEditType(null);
    setShowModal(true);
  }
  
  const openEditModal = (type: EnduranceTrainingTypeDto): void => {
    setEditType(type);
    setShowModal(true);
  }
  
  const renderCreateButton = (): ReactElement => {
    return (
      <PrimaryButton
        className="ms-auto"
        onClick={openCreateModal}
        disabled={isLoading}>
        {i18next.t('generic.create')}
      </PrimaryButton>
    )
  }

  const renderTable = (types: EnduranceTrainingTypeDto[], canEdit: boolean, showCreateButton: boolean): ReactElement => {
    return (
      <EnduranceTrainingTypeTable
        types={types}
        isLoading={isLoading}
        toolbar={showCreateButton ? renderCreateButton() : undefined}
        onClick={canEdit ? openEditModal : undefined}
      />
    )
  }

  const renderAdminContent = (): ReactElement => {
    return (
      <Tabs
        activeKey={activeKey}
        onSelect={(key) => {
          if (isScopeTabKey(key)) {
            setActiveKey(key);
          }
        }}
        className="mb-3 pt-3"
        justify
        mountOnEnter
        unmountOnExit>
        <Tab
          eventKey="tenant"
          title={`${i18next.t('adminEnduranceTrainingTypes.tenantTypes')} (${tenantTypes.length})`}>
          {renderTable(tenantTypes, true, true)}
        </Tab>
        <Tab 
          eventKey="core"
          title={`${i18next.t('adminEnduranceTrainingTypes.coreTypes')} (${coreTypes.length})`}>
          {renderTable(coreTypes, false, false)}
        </Tab>
      </Tabs>
    )
  }
  
  const renderSuperAdminContent = (): ReactElement => {
    return (
      <div className="pt-3">
        {renderTable(types, true, true)}
      </div>
    )
  }
  
  return (
    <>
      <EnduranceTrainingTypeModal 
        show={showModal} 
        entityScope={modalEntityScope} 
        enduranceTrainingType={editType ?? undefined}
        units={units} 
        onClose={closeModal} 
        onSave={() => fetchTypes()}
      />

      {isAdminMode ? renderAdminContent() : renderSuperAdminContent()}
    </>
  )
}

export default EnduranceTrainingTypeManagement;